import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PaEmptyState,
  PaNavigationContextBanner,
  PaPageHeader,
  PaSkeleton,
  PaTabs,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  cancelPlatformTenantSubscription,
  extendPlatformTenantTrial,
  fetchPlatformBillingOverview,
  sendPlatformTenantOutstandingReminder,
  setPlatformTenantReadOnly,
  syncPlatformStripeInvoices,
} from "store/services/platformAdmin.services";
import {
  buildBillingAlertSections,
  buildFinancialMetricCards,
  buildPlanBreakdownRows,
  FINANCIAL_OVERVIEW_SECTION_BY_TAB,
  FINANCIAL_OVERVIEW_SUMMARY_SECTION,
  FINANCIAL_OVERVIEW_TAB_IDS,
  FINANCIAL_OVERVIEW_TABS,
  getFinancialOverviewSubtitle,
  resolveCollectionsBadgeCount,
  resolveFinancialOverviewTab,
  resolveFollowUpBadgeCount,
} from "./billingOverview.utils";
import FinancialAttentionPanel from "./FinancialAttentionPanel";
import FinancialCollectionsPanel from "./FinancialCollectionsPanel";
import FinancialOverviewCharts from "./FinancialOverviewCharts";
import FinancialOverviewSummary from "./FinancialOverviewSummary";
import FinancialPlansTable from "./FinancialPlansTable";
import FinancialRecentPayments from "./FinancialRecentPayments";
import BillingReminderConfirmModal from "./BillingReminderConfirmModal";
import TenantActionModal from "../Tenants/TenantActionModal";
import TenantExtendTrialModal from "../Tenants/TenantExtendTrialModal";
import { getNavigationContext } from "../platformAdminNavigation.utils";

const FinancialOverview = () => {
  const canViewBilling = platformRoleAccess("platform.billing.view");
  const canManagePlans = platformRoleAccess("platform.plans.manage");
  const canManageBilling = platformRoleAccess("platform.billing.manage");
  const canReadOnly = platformRoleAccess("platform.tenant.read_only");
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const tabFromUrl = searchParams.get("tab");
  const navigationContext = useMemo(() => getNavigationContext(focus), [focus]);
  const activeMetricId = useMemo(() => {
    if (focus === "collected_30d") {
      return "collected_last_30_days";
    }

    if (focus === "failed_payments") {
      return "failed_payments";
    }

    if (focus === "wildcard_accounts") {
      return "wildcard_accounts";
    }

    return focus;
  }, [focus]);
  const [activeTab, setActiveTab] = useState(() => {
    if (tabFromUrl && FINANCIAL_OVERVIEW_TABS.some((tab) => tab.id === tabFromUrl)) {
      return tabFromUrl;
    }

    return resolveFinancialOverviewTab(focus);
  });
  const [billing, setBilling] = useState(null);
  const [loadedSections, setLoadedSections] = useState(() => new Set());
  const [sectionLoading, setSectionLoading] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [extendTrialTarget, setExtendTrialTarget] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const loadedSectionsRef = useRef(new Set());

  const queuePermissions = useMemo(
    () => ({
      readOnly: canReadOnly,
      billingManage: canManageBilling,
    }),
    [canManageBilling, canReadOnly]
  );

  const loadSection = useCallback(async (section, { force = false } = {}) => {
    if (!force && loadedSectionsRef.current.has(section)) {
      return;
    }

    setSectionLoading((prev) => ({ ...prev, [section]: true }));
    setSectionErrors((prev) => ({ ...prev, [section]: null }));

    try {
      const data = await fetchPlatformBillingOverview(section);
      setBilling((prev) => ({ ...(prev || {}), ...(data || {}) }));
      loadedSectionsRef.current.add(section);
      setLoadedSections(new Set(loadedSectionsRef.current));
    } catch (err) {
      setSectionErrors((prev) => ({
        ...prev,
        [section]: err?.message || "Failed to load billing data",
      }));
    } finally {
      setSectionLoading((prev) => ({ ...prev, [section]: false }));
    }
  }, []);

  const reloadLoadedSections = useCallback(async () => {
    loadedSectionsRef.current = new Set();
    setLoadedSections(new Set());

    await loadSection(FINANCIAL_OVERVIEW_SUMMARY_SECTION, { force: true });

    const activeSection = FINANCIAL_OVERVIEW_SECTION_BY_TAB[activeTab];
    if (activeSection) {
      await loadSection(activeSection, { force: true });
    }
  }, [activeTab, loadSection]);

  useEffect(() => {
    if (!canViewBilling) {
      return undefined;
    }

    loadSection(FINANCIAL_OVERVIEW_SUMMARY_SECTION);
  }, [canViewBilling, loadSection]);

  useEffect(() => {
    if (!canViewBilling) {
      return undefined;
    }

    const section = FINANCIAL_OVERVIEW_SECTION_BY_TAB[activeTab];
    if (section) {
      loadSection(section);
    }
  }, [activeTab, canViewBilling, loadSection]);

  useEffect(() => {
    if (tabFromUrl && FINANCIAL_OVERVIEW_TABS.some((tab) => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
      return;
    }

    if (focus) {
      setActiveTab(resolveFinancialOverviewTab(focus));
    }
  }, [focus, tabFromUrl]);

  useEffect(() => {
    const summaryLoading = sectionLoading[FINANCIAL_OVERVIEW_SUMMARY_SECTION];
    if (summaryLoading || !window.location.hash) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      document.querySelector(window.location.hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, billing, sectionLoading]);

  const summaryLoading = Boolean(sectionLoading[FINANCIAL_OVERVIEW_SUMMARY_SECTION]);
  const activeSection = FINANCIAL_OVERVIEW_SECTION_BY_TAB[activeTab];
  const activeTabLoading = activeSection ? Boolean(sectionLoading[activeSection]) : false;
  const summaryError = sectionErrors[FINANCIAL_OVERVIEW_SUMMARY_SECTION];
  const activeTabError = activeSection ? sectionErrors[activeSection] : null;
  const followUpLoaded = loadedSections.has(FINANCIAL_OVERVIEW_SECTION_BY_TAB.followUp);
  const collectionsLoaded = loadedSections.has(FINANCIAL_OVERVIEW_SECTION_BY_TAB.collections);

  const metricCards = useMemo(
    () =>
      buildFinancialMetricCards(
        billing?.stats,
        billing?.estimated_mrr,
        billing?.revenue_summary
      ),
    [billing]
  );
  const planRows = useMemo(
    () => buildPlanBreakdownRows(billing?.plan_breakdown),
    [billing]
  );
  const alertSections = useMemo(() => buildBillingAlertSections(billing), [billing]);
  const followUpCount = useMemo(
    () => resolveFollowUpBadgeCount(billing, alertSections, followUpLoaded),
    [alertSections, billing, followUpLoaded]
  );
  const collectionsCount = useMemo(
    () => resolveCollectionsBadgeCount(billing, collectionsLoaded),
    [billing, collectionsLoaded]
  );

  const handleQueueAction = useCallback((actionId, row) => {
    const modalConfig = {
      read_only_on: {
        type: "read_only",
        tenantId: row.tenantId,
        tenantName: row.name,
        enabled: true,
        title: "Enable read-only",
        description: `Block writes for ${row.name} while keeping logins active.`,
        confirmLabel: "Enable read-only",
      },
      read_only_off: {
        type: "read_only",
        tenantId: row.tenantId,
        tenantName: row.name,
        enabled: false,
        title: "Lift read-only",
        description: `Allow writes again for ${row.name}.`,
        confirmLabel: "Lift read-only",
      },
      cancel_subscription_period_end: {
        type: "cancel_subscription_period_end",
        tenantId: row.tenantId,
        tenantName: row.name,
        title: "Cancel at period end",
        description: `Schedule Stripe cancellation for ${row.name} after the current billing period.`,
        confirmLabel: "Schedule cancellation",
        destructive: true,
      },
    }[actionId];

    if (actionId === "extend_trial") {
      setExtendTrialTarget(row);
      return;
    }

    if (actionId === "send_outstanding_reminder") {
      setReminderModal({
        tenantId: row.tenantId,
        tenantName: row.name,
        title: "Email outstanding reminder",
        description: `Send a payment reminder email to ${row.name} listing all open invoices with pay links and PDF attachments.`,
        confirmLabel: "Send reminder email",
      });
      return;
    }

    if (modalConfig) {
      setActionModal(modalConfig);
    }
  }, []);

  const handleActionSubmit = async (reason) => {
    if (!actionModal) {
      return;
    }

    setActionSubmitting(true);

    try {
      switch (actionModal.type) {
        case "read_only":
          await setPlatformTenantReadOnly(
            actionModal.tenantId,
            actionModal.enabled,
            reason
          );
          toast.success(
            actionModal.enabled ? "Read-only mode enabled" : "Read-only mode lifted"
          );
          break;
        case "cancel_subscription_period_end":
          await cancelPlatformTenantSubscription(
            actionModal.tenantId,
            reason,
            "period_end"
          );
          toast.success("Subscription scheduled to cancel at period end");
          break;
        default:
          break;
      }

      setActionModal(null);
      await reloadLoadedSections();
    } catch (err) {
      toast.error(err?.message || "Action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReminderConfirm = async () => {
    if (!reminderModal?.tenantId) {
      return;
    }

    setActionSubmitting(true);

    try {
      const result = await sendPlatformTenantOutstandingReminder(reminderModal.tenantId);
      toast.success(
        `Reminder sent for ${result?.invoice_count || "all"} invoice(s) to ${
          result?.recipient_email || "billing contact"
        }`
      );
      setReminderModal(null);
      await reloadLoadedSections();
    } catch (err) {
      toast.error(err?.message || "Action failed");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleExtendTrialSubmit = async ({ reason, trialEndDate }) => {
    if (!extendTrialTarget?.tenantId) {
      return;
    }

    setActionSubmitting(true);

    try {
      await extendPlatformTenantTrial(extendTrialTarget.tenantId, reason, trialEndDate);
      toast.success("Trial extended");
      setExtendTrialTarget(null);
      await reloadLoadedSections();
    } catch (err) {
      toast.error(err?.message || "Failed to extend trial");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === FINANCIAL_OVERVIEW_TAB_IDS.payments) {
            next.delete("tab");
          } else {
            next.set("tab", tabId);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSyncInvoices = async () => {
    setSyncing(true);

    try {
      const result = await syncPlatformStripeInvoices();
      toast.success(result?.message || "Stripe invoices synced");
      await reloadLoadedSections();
    } catch (err) {
      toast.error(err?.message || "Failed to sync Stripe invoices");
    } finally {
      setSyncing(false);
    }
  };

  const tabs = useMemo(
    () =>
      FINANCIAL_OVERVIEW_TABS.map((tab) => ({
        ...tab,
        badge:
          tab.id === FINANCIAL_OVERVIEW_TAB_IDS.followUp
            ? followUpCount
            : tab.id === FINANCIAL_OVERVIEW_TAB_IDS.collections
              ? collectionsCount
              : null,
      })),
    [collectionsCount, followUpCount]
  );

  const renderActiveTabPanel = () => {
    if (activeTabError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {activeTabError}
        </div>
      );
    }

    if (activeTabLoading) {
      if (activeTab === FINANCIAL_OVERVIEW_TAB_IDS.trends) {
        return (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <PaSkeleton key={index} variant="card" />
            ))}
          </div>
        );
      }

      return <PaSkeleton variant="table" lines={6} />;
    }

    switch (activeTab) {
      case FINANCIAL_OVERVIEW_TAB_IDS.payments:
        return <FinancialRecentPayments recentPayments={billing?.recent_payments} loading={false} />;
      case FINANCIAL_OVERVIEW_TAB_IDS.followUp:
        return followUpCount > 0 ? (
          <FinancialAttentionPanel
            sections={alertSections}
            loading={false}
            permissions={queuePermissions}
            onAction={handleQueueAction}
          />
        ) : (
          <PaEmptyState
            title="Billing queue clear"
            message="No tenants need follow-up right now."
            description="Trials expiring, failed payments, overdue invoices, and billing exceptions will appear here."
          />
        );
      case FINANCIAL_OVERVIEW_TAB_IDS.collections:
        return (
          <FinancialCollectionsPanel
            collections={billing?.collections}
            summary={billing?.collections_summary}
            permissions={queuePermissions}
            onAction={handleQueueAction}
          />
        );
      case FINANCIAL_OVERVIEW_TAB_IDS.trends:
        return <FinancialOverviewCharts billing={billing} />;
      case FINANCIAL_OVERVIEW_TAB_IDS.plans:
        return <FinancialPlansTable rows={planRows} loading={false} />;
      default:
        return null;
    }
  };

  if (!canViewBilling) {
    return (
      <PaAccessDenied
        title="Financial overview"
        subtitle="Revenue, subscriptions, and billing health"
        description="You need platform.billing.view."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Financial overview"
        subtitle={getFinancialOverviewSubtitle(billing?.computed_at, billing?.revenue_summary)}
        className="mb-4"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSyncInvoices}
              disabled={syncing || summaryLoading}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
            >
              {syncing ? "Syncing…" : "Sync Stripe invoices"}
            </button>
            <Link
              to="/stripe?tab=customers"
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Stripe customers →
            </Link>
            {canManagePlans ? (
              <Link
                to="/stripe?tab=plans"
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Plans & catalog →
              </Link>
            ) : null}
          </div>
        }
      />

      <div className="space-y-4">
        {summaryError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {summaryError}
          </div>
        ) : null}

        {navigationContext ? (
          <PaNavigationContextBanner
            title={navigationContext.title}
            description={navigationContext.description}
          />
        ) : null}

        <FinancialOverviewSummary
          cards={metricCards}
          loading={summaryLoading}
          activeMetricId={activeMetricId}
        />

        <div>
          <PaTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
          <div className="mt-4">{renderActiveTabPanel()}</div>
        </div>
      </div>

      <TenantActionModal
        open={Boolean(actionModal)}
        onClose={() => setActionModal(null)}
        title={actionModal?.title}
        description={actionModal?.description}
        confirmLabel={actionModal?.confirmLabel}
        destructive={actionModal?.destructive}
        submitting={actionSubmitting}
        onSubmit={handleActionSubmit}
      />

      <BillingReminderConfirmModal
        open={Boolean(reminderModal)}
        onClose={() => setReminderModal(null)}
        title={reminderModal?.title}
        description={reminderModal?.description}
        confirmLabel={reminderModal?.confirmLabel}
        submitting={actionSubmitting}
        onConfirm={handleReminderConfirm}
      />

      <TenantExtendTrialModal
        open={Boolean(extendTrialTarget)}
        onClose={() => setExtendTrialTarget(null)}
        currentTrialEndDate={extendTrialTarget?.trial_end_date}
        submitting={actionSubmitting}
        onSubmit={handleExtendTrialSubmit}
      />
    </PlatformAdminLayout>
  );
};

export default FinancialOverview;
